import {EvalEnv} from '@packages/systems/wfdl';
import {singleMemo, weakMemo, weakMemoMulti} from '@packages/utilities/memo';
import {force, type Lazy} from '@packages/utilities/lazy';
import {hrefAttr} from '@packages/systems/core/utils/HtmlUtils';
import type {Environment} from '@packages/systems/core-constants';
import {
  addAssetUtilsToEnv,
  createAssetUrlResolver,
  type AssetUrlResolver,
} from '../Asset';
import {
  addBasicLinkUtilsToEnv,
  createPageUrlResolver,
  createSectionIdResolver,
} from '@packages/systems/core/plugins';
// Mirrors whatever `createAssetUrlResolver` accepts so we stay in lockstep
// with the upstream contract -- canvas-side and SSR-side render entry
// points already produce assets in this shape.
type AssetCdnMap = Parameters<typeof createAssetUrlResolver>[0];

// Stable per-runtime-environment sanitizer. Module-level + singleMemo on
// the enum value so the inner arrow has a stable identity across
// renders -- otherwise addBasicLinkUtilsToEnv misses every render and
// rebuilds the env on every canvas tick.
const getSanitizeHref = singleMemo(
  (runtimeEnvironment: Environment) =>
    (href: string): string =>
      hrefAttr(href, runtimeEnvironment).href
);

type BasicImplicitConversionUtilsArgs = {
  localizedAssets: AssetCdnMap;
  idAttrMap: Lazy<Record<string, string>>;
  hrefMap: Record<string, string> | null | undefined;
  runtimeEnvironment: Environment;
};

type IdAttrMapRef = {
  current: Lazy<Record<string, string>>;
};

// Bundles Basic implicit text conversions for render entry points:
// Asset::imageToText and Basic::linkToText, both rebuilt with concrete
// resolvers. Pulls stable ids -- idAttrMap, createAssetUrlResolver /
// createPageUrlResolver (weakMemo'd by their maps), getSanitizeHref
// (singleMemo'd by runtimeEnvironment) -- so the returned env reaches the
// inner weakMemo cache when nothing changed.
export const addBasicImplicitConversionUtilsToEnv = (
  env: EvalEnv.EvalEnvType,
  {
    localizedAssets,
    idAttrMap,
    hrefMap,
    runtimeEnvironment,
  }: BasicImplicitConversionUtilsArgs
): EvalEnv.EvalEnvType => {
  const assetUrlResolver: AssetUrlResolver =
    createAssetUrlResolver(localizedAssets);
  const next = addAssetUtilsToEnv(env, assetUrlResolver);
  const hrefMapOrEmpty = hrefMap || EMPTY_HREF_MAP;
  const sanitizeHref = getSanitizeHref(runtimeEnvironment);
  const idAttrMapRef =
    getIdAttrMapRef(assetUrlResolver)(hrefMapOrEmpty)(sanitizeHref);
  idAttrMapRef.current = idAttrMap;

  return addBasicLinkUtilsToEnv(
    next,
    getResolversBundle(
      assetUrlResolver,
      idAttrMapRef,
      hrefMapOrEmpty,
      sanitizeHref
    )
  );
};

const EMPTY_HREF_MAP: Record<string, string> = {};
const EMPTY_ID_ATTR_MAP: Lazy<Record<string, string>> = () => ({});

const getIdAttrMapRef = weakMemo((_assetUrlResolver: AssetUrlResolver) =>
  weakMemo((_hrefMap: Record<string, string>) =>
    weakMemo(
      (_sanitizeHref: (href: string) => string): IdAttrMapRef => ({
        current: EMPTY_ID_ATTR_MAP,
      })
    )
  )
);

// Reference-keyed cache for the resolvers bundle. Ensures the outer
// addBasicLinkUtilsToEnv weakMemo sees the same bundle reference
// when hrefMap/assets are stable across renders. The idAttrMap ref keeps the
// resolver identity stable while still reading the latest element id map.
const getResolversBundle = weakMemoMulti(
  (
    assetUrlResolver: AssetUrlResolver,
    idAttrMapRef: IdAttrMapRef,
    hrefMap: Record<string, string>,
    sanitizeHref: (href: string) => string
  ) => ({
    resolveAssetUrl: assetUrlResolver,
    resolvePageUrl: createPageUrlResolver(hrefMap),
    resolveSectionId: createSectionIdResolver(() =>
      force(idAttrMapRef.current)
    ),
    sanitizeHref,
  })
);
